import findPersonaByEmail from './findPersonaByEmail';
import { buildProvenanceStamp } from '../meetstandaard/buildMatrixRowPayload';

/**
 * Builds the full set of Firestore documents an import must create/reuse, as a PURE plan
 * (no Firestore calls, no timestamps). The executor stamps `Timestamp` and converts
 * `timestampRaw` → a real Timestamp at write time.
 *
 * Reuse rules:
 * - Effect/field/rows/columns are reused by `MSIId` (field scoped to this research's
 *   questionnaire) so a re-import doesn't duplicate structure.
 * - A persona is reused when its email already exists (existing PersonaData Email row, or
 *   one created earlier in the same run); otherwise a new persona + PersonaData Email row.
 *
 * @typedef {object} BuildImportPlanArgs
 * @property {{metaCols: object, dataRows: Array<Array<*>>}} parsed - Output of parseDatasetWorkbook.
 * @property {Array<{colIndex:number, effect:object|null, question:object|null}>} columnMappings - Final (post-edit) per-column mapping.
 * @property {{ID:string, QuestionnaireID?:string, TargetgroupID?:string}} research - The target research.
 * @property {string} momentId - The MeasureMoment.ID the import is scoped to.
 * @property {string} client - Tenant id (CompagnyID/Compagny).
 * @property {{effects:Array, fields:Array, rows:Array, columns:Array, personaData:Array, personas?:Array}} existing - Pre-fetched docs for reuse. When `personas` is supplied, only PersonaData whose persona still exists is used for email matching (orphaned rows are ignored).
 * @property {() => string} newId - Id generator (uuid in prod; deterministic in tests).
 * @property {Object<string,string>} [personaOverrides] - Per-respondent persona choice from the
 *   import UI, keyed by the lowercased email. Value is an existing `Persona.ID` to attach the
 *   data to, or `'new'` to force a fresh persona. Rows without an entry fall back to email matching.
 *
 * @param {BuildImportPlanArgs} args
 * @returns {object} The write plan.
 */
const buildImportPlan = ({ parsed, columnMappings, research, momentId, client, existing, newId, personaOverrides }) => {
    const { metaCols, dataRows } = parsed;
    const ex = existing || {};

    // Only match emails against personas that still exist. Deleting a persona leaves its
    // PersonaData email rows behind (orphans); without this filter those orphans would
    // shadow a deleted persona and stop a fresh one from being created on re-import.
    const livePersonaIds = ex.personas ? new Set(ex.personas.map((p) => p.ID)) : null;
    const matchablePersonaData = livePersonaIds
        ? (ex.personaData || []).filter((pd) => livePersonaIds.has(pd.PersonaID))
        : (ex.personaData || []);

    // --- indexes for reuse -------------------------------------------------
    const effectIdByMsi = new Map((ex.effects || []).map((e) => [e.MSIId, e.ID]));
    const fieldByMsi = new Map(
        (ex.fields || [])
            .filter((f) => (research.QuestionnaireID ? f.QuestionnaireID === research.QuestionnaireID : false))
            .map((f) => [f.MSIId, f.ID])
    );
    const rowIdByFieldMsi = new Map((ex.rows || []).map((r) => [`${r.Field}|${r.MSIId}`, r.ID]));
    // The whole existing row, so a response can inherit the stamp that row was
    // adopted under. Re-deriving it from today's catalogue would relabel older
    // answers with a version they were never given under.
    const rowByFieldMsi = new Map((ex.rows || []).map((r) => [`${r.Field}|${r.MSIId}`, r]));

    /**
     * Reads the provenance an existing row already carries. Returns null when
     * it has none (adopted before the stamp existed, or before the backfill) —
     * absent is honest, and the backfill can still find it.
     *
     * @param {object} row - MatrixQuestionRows document
     * @returns {object|null}
     */
    const readStamp = (row) => {
        if (!row || !row.Standaard) return null;
        return {
            Standaard: row.Standaard,
            StandaardVersion: row.StandaardVersion || '',
            EffectUid: row.EffectUid || '',
            StellingNummer: Number.isFinite(row.StellingNummer) ? row.StellingNummer : null,
        };
    };
    const columnIdByFieldTitle = new Map((ex.columns || []).map((c) => [`${c.Field}|${String(c.Title)}`, c.ID]));

    // --- output buckets ----------------------------------------------------
    const effectsToCreate = [];
    const fieldsToCreate = [];
    const rowsToCreate = [];
    const columnsToCreate = [];
    const personasToCreate = [];
    const personaDataToCreate = [];
    const responsesToWrite = [];

    // --- questionnaire link ------------------------------------------------
    let questionnaireToCreate = null;
    let researchUpdate = null;
    let questionnaireId = research.QuestionnaireID || '';
    if (!questionnaireId) {
        questionnaireId = newId();
        questionnaireToCreate = {
            ID: questionnaireId,
            Compagny: client,
            CompagnyID: client,
            Title: `Vragenlijst onderzoek ${research.Title || ''}`.trim(),
            Description: '',
            Position: 1,
        };
        researchUpdate = { QuestionnaireID: questionnaireId };
    }

    const targetGroup = research.TargetgroupID || '';
    const fieldPositionBase = (ex.fields || []).length;

    // --- resolve structure per matched effect ------------------------------
    // For each matched column we cache where its response must point.
    /** @type {Map<number, {fieldId:string, fieldPosition:number, rowId:string, rowTitle:string, columnTitleToId:Map<string,string>}>} */
    const colTarget = new Map();
    /** @type {Map<string, {fieldId:string, fieldPosition:number, columnTitleToId:Map<string,string>}>} */
    const effectResolved = new Map();

    let newEffectOrdinal = 0;
    const matched = columnMappings.filter((m) => m.effect && m.question);

    matched.forEach((m) => {
        const msiEffect = m.effect.id;

        // Effect + field + columns (once per effect).
        let resolved = effectResolved.get(msiEffect);
        if (!resolved) {
            // Effect doc.
            let effectId = effectIdByMsi.get(msiEffect);
            if (!effectId) {
                effectId = newId();
                effectIdByMsi.set(msiEffect, effectId);
                effectsToCreate.push({
                    ID: effectId,
                    Effect: m.effect.name,
                    Description: m.effect.description || '',
                    MainEffect: m.effect.category || '',
                    Position: (ex.effects || []).length + effectsToCreate.length + 1,
                    MSIId: msiEffect,
                    Compagny: client,
                    CompagnyID: client,
                    Standard: 'true',
                    sectors: Array.isArray(m.effect.sectors) ? m.effect.sectors : [],
                    TargetGroup: targetGroup,
                });
            }

            // Field doc.
            let fieldId = fieldByMsi.get(msiEffect);
            const columnTitleToId = new Map();
            let fieldPosition;
            if (!fieldId) {
                fieldId = newId();
                fieldByMsi.set(msiEffect, fieldId);
                newEffectOrdinal += 1;
                fieldPosition = fieldPositionBase + newEffectOrdinal;
                fieldsToCreate.push({
                    ID: fieldId,
                    Compagny: client,
                    CompagnyID: client,
                    QuestionnaireID: questionnaireId,
                    Type: 'matrix-one',
                    Question: 'In welke mate ben je het eens met de volgende stellingen?',
                    Explainer: '',
                    ReachStart: 1,
                    ReachStartLable: '',
                    ReachEnd: 5,
                    ReachEndLabel: '',
                    Multiple: [],
                    SectionID: '',
                    MSIId: msiEffect,
                    EffectId: [effectId],
                    Indicator: 'true',
                    LiveReportHidden: false,
                    Standard: 'true',
                    Position: fieldPosition,
                    IndicatorPosition: fieldPosition,
                    TargetGroup: targetGroup,
                });
                // 5 scale columns.
                for (let i = 1; i <= 5; i += 1) {
                    const columnId = newId();
                    columnTitleToId.set(String(i), columnId);
                    columnsToCreate.push({
                        ID: columnId,
                        Title: i,
                        Field: fieldId,
                        Compagny: client,
                        CompagnyID: client,
                        Position: i,
                    });
                }
            } else {
                // Reused field: read its position + existing columns.
                const exField = (ex.fields || []).find((f) => f.ID === fieldId);
                fieldPosition = exField?.Position || 1;
                for (let i = 1; i <= 5; i += 1) {
                    const existingCol = columnIdByFieldTitle.get(`${fieldId}|${String(i)}`);
                    if (existingCol) {
                        columnTitleToId.set(String(i), existingCol);
                    } else {
                        const columnId = newId();
                        columnTitleToId.set(String(i), columnId);
                        columnsToCreate.push({
                            ID: columnId,
                            Title: i,
                            Field: fieldId,
                            Compagny: client,
                            CompagnyID: client,
                            Position: i,
                        });
                    }
                }
            }

            resolved = { fieldId, fieldPosition, columnTitleToId };
            effectResolved.set(msiEffect, resolved);
        }

        // Row (once per question).
        const rowKey = `${resolved.fieldId}|${m.question.id}`;
        let rowId = rowIdByFieldMsi.get(rowKey);
        // An existing row keeps the stamp it was adopted under, even when that
        // is an older version than the catalogue serves today. Only a row this
        // import materialises gets a freshly derived one.
        const existingRow = rowByFieldMsi.get(rowKey);
        let stamp = existingRow ? readStamp(existingRow) : null;
        if (!rowId) {
            rowId = newId();
            stamp = buildProvenanceStamp(m.effect, m.question);
            rowIdByFieldMsi.set(rowKey, rowId);
            rowsToCreate.push({
                ID: rowId,
                Title: m.question.name,
                Field: resolved.fieldId,
                Compagny: client,
                CompagnyID: client,
                Position: rowsToCreate.filter((r) => r.Field === resolved.fieldId).length + 1,
                MSIId: m.question.id,
                posNeg: m.question.posNeg === 'negative' ? 'negative' : 'positive',
                TargetGroup: targetGroup,
                // The import is the third path that materialises rows, next to
                // AddEffectFlow and Decca. Not stamping here would keep feeding
                // the backfill new work forever.
                ...stamp,
            });
        }

        colTarget.set(m.colIndex, {
            fieldId: resolved.fieldId,
            fieldPosition: resolved.fieldPosition,
            rowId,
            rowTitle: m.question.name,
            stamp,
            columnTitleToId: resolved.columnTitleToId,
        });
    });

    // --- personas + responses, per data row --------------------------------
    /** @type {Map<string, string>} in-run email → personaId */
    const personaIdByEmail = new Map();

    const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';

    // The app groups responses into a single "submission" by their millisecond Timestamp
    // (e.g. ReserachResultsPersonas#sortForms). An import file usually carries one shared
    // timestamp for every row, which would collapse all respondents into one form. We hand
    // each respondent row a distinct sessionIndex so the executor can offset its Timestamp
    // by sessionIndex ms — keeping every submission separate.
    let sessionIndex = -1;

    // Creates a brand-new persona (+ its Email PersonaData row) and returns its id.
    const createPersona = (email) => {
        const id = newId();
        personasToCreate.push({
            ID: id,
            Name: email, // seed Name from the email until a human renames it
            Compagny: client,
            CompagnyID: client,
            Activities: [],
            Effects: [],
            Research: [],
            Position: personasToCreate.length + 1,
            Status: 'active',
            OutflowReason: '',
            InactiveReason: '',
            OutflowDate: null,
            InactiveDate: null,
        });
        personaDataToCreate.push({
            ID: newId(),
            PersonaID: id,
            Title: 'Email',
            Value: email,
            Category: 'data',
            Compagny: client,
            CompagnyID: client,
            Position: 1,
        });
        return id;
    };

    (dataRows || []).forEach((row) => {
        const email = String(row[metaCols.personaId] ?? '').trim();
        if (!email) return; // a row without a respondent key is unusable

        sessionIndex += 1;

        const emailKey = email.toLowerCase();
        let personaId = personaIdByEmail.get(emailKey);
        if (!personaId) {
            // The import UI may pin each respondent to a chosen persona (or force a new one).
            // Without a choice we fall back to matching the email against existing personas.
            const override = personaOverrides ? personaOverrides[emailKey] : undefined;
            if (override && override !== 'new') {
                personaId = override; // attach to the explicitly chosen existing persona
            } else if (override === 'new') {
                personaId = createPersona(email); // forced fresh persona
            } else {
                personaId = findPersonaByEmail(matchablePersonaData, email) || createPersona(email);
            }
            personaIdByEmail.set(emailKey, personaId);
        }

        const sessionId = newId();
        const version = metaCols.version !== undefined ? row[metaCols.version] : '';
        const timestampRaw = metaCols.timestamp !== undefined ? row[metaCols.timestamp] : '';

        colTarget.forEach((target, colIndex) => {
            const value = row[colIndex];
            if (isBlank(value)) return; // skip empty cells

            const input = String(value);
            responsesToWrite.push({
                ID: newId(),
                FieldID: target.fieldId,
                FieldTitle: 'In welke mate ben je het eens met de volgende stellingen?',
                Input: input,
                CompagnyID: client,
                QuestionannaireID: questionnaireId, // intentional typo — matches existing schema
                MomentID: momentId,
                ResearchID: research.ID,
                FormID: sessionId,
                SessionID: sessionId,
                OptionType: '',
                Persona: personaId,
                RowTitle: target.rowTitle,
                RowID: target.rowId,
                // Same provenance as the row this answer points at, mirroring
                // what saveQuestionnaireResponse does server-side for the
                // public form. Absent when the row carries none.
                ...(target.stamp || {}),
                FieldType: 'matrix-one',
                ColumnID: target.columnTitleToId.get(input) || '',
                Position: target.fieldPosition,
                MeetstandaardVersie: isBlank(version) ? '' : String(version),
                timestampRaw,
                sessionIndex,
            });
        });
    });

    return {
        questionnaireToCreate,
        researchUpdate,
        effectsToCreate,
        fieldsToCreate,
        rowsToCreate,
        columnsToCreate,
        personasToCreate,
        personaDataToCreate,
        responsesToWrite,
    };
};

export default buildImportPlan;
